import React from 'react';

const Chart = ({ data, type = 'bar', title, height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center" style={{ height }}>
        <p className="text-gray-500">No data available for chart</p>
      </div>
    );
  }

  const renderBarChart = () => {
    const maxValue = Math.max(...data.map(item => item.value));

    return (
      <div className="space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center space-x-3">
            <div className="w-20 text-sm text-gray-600 truncate">
              {item.label}
            </div>
            <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
              <div
                className="bg-blue-500 h-6 rounded-full transition-all duration-300"
                style={{ width: `${(item.value / maxValue) * 100}%` }}
              />
              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                {item.value}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPieChart = () => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;

    return (
      <div className="relative" style={{ width: height, height }}>
        <svg width={height} height={height} className="transform -rotate-90">
          {data.map((item, index) => {
            const percentage = item.value / total;
            const angle = percentage * 360;
            const startAngle = currentAngle;
            currentAngle += angle;

            const x1 = height / 2 + (height / 2 - 20) * Math.cos(startAngle * Math.PI / 180);
            const y1 = height / 2 + (height / 2 - 20) * Math.sin(startAngle * Math.PI / 180);
            const x2 = height / 2 + (height / 2 - 20) * Math.cos(currentAngle * Math.PI / 180);
            const y2 = height / 2 + (height / 2 - 20) * Math.sin(currentAngle * Math.PI / 180);

            const largeArcFlag = angle > 180 ? 1 : 0;

            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

            return (
              <path
                key={index}
                d={`M ${height / 2} ${height / 2} L ${x1} ${y1} A ${height / 2 - 20} ${height / 2 - 20} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                fill={colors[index % colors.length]}
                stroke="white"
                strokeWidth="2"
              />
            );
          })}
        </svg>

        {/* Legend */}
        <div className="absolute bottom-0 left-0 right-0 flex flex-wrap justify-center space-x-4">
          {data.map((item, index) => {
            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
            return (
              <div key={index} className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span className="text-xs text-gray-600">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLineChart = () => {
    const numericData = data.map(item => Number(item.value) || 0);
    let maxValue = Math.max(...numericData);
    let minValue = Math.min(...numericData);
    if (maxValue === minValue) {
      maxValue += 10;
      minValue = Math.max(0, minValue - 10);
    }
    const range = maxValue - minValue;

    const points = data.map((item, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = 95 - (((Number(item.value) || 0) - minValue) / range) * 90;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="relative w-full h-full flex flex-col pt-2">
        <div className="relative flex-grow">
          <svg className="absolute inset-0 w-full h-full overflow-visible">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full overflow-visible">
              <polyline
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
                vectorEffect="non-scaling-stroke"
                points={points}
              />
            </svg>
            {data.map((item, index) => {
              const x = (index / (data.length - 1)) * 100;
              const y = 95 - (((Number(item.value) || 0) - minValue) / range) * 90;
              return (
                <circle
                  key={index}
                  cx={`${x}%`}
                  cy={`${y}%`}
                  r="5"
                  fill="#ffffff"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  className="transition-all duration-300"
                />
              );
            })}
          </svg>
        </div>

        {/* Labels */}
        <div className="relative h-6 mt-4 flex justify-between text-xs text-gray-500 font-medium px-1">
          {data.map((item, index) => (
            <span key={index} className="text-center w-12 -ml-6 first:ml-0 first:text-left last:-mr-6 last:text-right">
              {item.label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return renderBarChart();
      case 'pie':
        return renderPieChart();
      case 'line':
        return renderLineChart();
      default:
        return renderBarChart();
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      {title && (
        <h3 className="text-lg font-semibold mb-4 text-gray-900">{title}</h3>
      )}
      <div style={{ height }}>
        {renderChart()}
      </div>
    </div>
  );
};

export default Chart;
